import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Bell } from "lucide-react";
import { ReactNode } from "react";
import { toast } from "sonner";

import { ToastType } from "@/enums";

const BASE_STYLES = {
  borderRadius: "8px",
  padding: "12px 16px",
  fontSize: "14px",
  fontWeight: "450",
  lineHeight: "20px",
  letterSpacing: "-0.006em",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',

  border: "1px solid",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#ffffff",
  color: "#0f0f0f",
};

const DEFAULT_OPTIONS = {
  duration: 4000,
  closeButton: false,
};

const BASE_ACTION_BUTTON_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgb(107,114,128)",
  fontWeight: 500,
  fontSize: "13px",
  padding: 0,
  cursor: "pointer",
  marginLeft: "12px",
};

// Icon components
const SuccessIcon = () => (
  <CheckCircleIcon className="h-4 w-4 text-green-600" style={{ flexShrink: 0, color: "#238346" }} />
);

const WarningIcon = () => (
  <ExclamationTriangleIcon
    className="h-4 w-4 text-orange-600"
    style={{ flexShrink: 0, color: "#D9730D" }}
  />
);

const ErrorIcon = () => (
  <XCircleIcon className="h-4 w-4 text-red-600" style={{ flexShrink: 0, color: "#DF3443" }} />
);

const NotificationIcon = () => (
  <Bell className="h-4 w-4 text-blue-600" style={{ flexShrink: 0, color: "#2563EB" }} />
);

// Toast type configurations
const TOAST_CONFIGS = {
  [ToastType.success]: {
    method: toast.success,
    borderColor: "rgba(35, 131, 70, 0.14)",
    icon: <SuccessIcon />,
  },
  [ToastType.warning]: {
    method: toast.warning,
    borderColor: "rgba(217, 115, 13, 0.14)",
    icon: <WarningIcon />,
  },
  [ToastType.error]: {
    method: toast.error,
    borderColor: "rgba(223, 52, 67, 0.14)",
    icon: <ErrorIcon />,
  },
  [ToastType.notification]: {
    method: toast,
    borderColor: "rgba(37, 99, 235, 0.14)",
    icon: <NotificationIcon />,
  },
};

interface DisplayToastOptions {
  url?: string;
  id?: string | number;
  description?: ReactNode;
  duration?: number;
  closeButton?: boolean;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  actionButtonStyle?: React.CSSProperties;
}

export const displayToast = (
  message: ReactNode,
  type: ToastType,
  options?: DisplayToastOptions | string,
): void => {
  const opts: DisplayToastOptions =
    typeof options === "string" ? { url: options } : (options ?? {});
  const config = TOAST_CONFIGS[type] || TOAST_CONFIGS[ToastType.error];

  const toastOptions = {
    ...DEFAULT_OPTIONS,
    ...(opts.id !== undefined && { id: opts.id }),
    ...(opts.description !== undefined && { description: opts.description }),
    ...(opts.duration !== undefined && { duration: opts.duration }),
    ...(opts.closeButton !== undefined && { closeButton: opts.closeButton }),
    ...(opts.action !== undefined && {
      action: opts.action,
      actionButtonStyle: {
        ...BASE_ACTION_BUTTON_STYLE,
        ...((opts.actionButtonStyle || {}) as React.CSSProperties),
      },
    }),
    onClick: opts.url ? () => (window.location.href = opts.url!) : undefined,
    style: {
      ...BASE_STYLES,
      borderColor: config.borderColor,
    },
    icon: opts.icon ?? config.icon,
  };

  config.method(message, toastOptions);
};

// Utility functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayErrorToast = (error: any) => {
  let errorStr: string;

  if (typeof error === "string") {
    errorStr = error;
  } else if (error?.response?.data) {
    errorStr = JSON.stringify(error.response.data);
  } else if (error?.message) {
    errorStr = error.message;
  } else {
    errorStr = JSON.stringify(error);
  }

  displayToast(errorStr, ToastType.error);
};

export const displayPromiseToast = <T,>(
  promise: Promise<T>,
  messages: {
    loading?: ReactNode;
    success?: ReactNode | ((data: T) => ReactNode);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: ReactNode | ((error: any) => ReactNode);
  },
) => {
  const loadingMessage = messages.loading || "Loading...";
  const successMessage = messages.success || "Success!";
  const errorMessage = messages.error || "Something went wrong";

  return toast.promise(promise, {
    loading: loadingMessage,
    success: (data: T) => {
      const message = typeof successMessage === "function" ? successMessage(data) : successMessage;
      return message;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: (error: any) => {
      const message = typeof errorMessage === "function" ? errorMessage(error) : errorMessage;
      return message;
    },
  });
};
